import { pipe, Runtime } from "fp";
import React from "react";

import * as Data from "~/Data";
import * as Log from "~/Log";
import { Remote } from "~/Remote";
import { Time } from "~/Time";

export * as Request from "./Request";
export * as Description from "./Description";

export type Violation = Time.Interval & {
  readonly limit: Time.Duration;
  readonly container: Container.Container;
};

export const use = ({
  driver,
  dateTime,
}: Log.Identity): Data.Remote<readonly Log.Violation.Violation[]> =>
  Remote.Query.use<readonly Log.Violation.Violation[]>(
    Log.Violation.Request.Query.key({
      id: Log.Violation.Request.Query.id({ driver, dateTime }),
    }),
    Log.Violation.Request.Query.request({
      driver,
      dateTime,
    }),
    {
      keepPreviousData: true,
      staleTime: Time.Duration.minutes(5).duration,
    }
  );

export const useCountFromActiveLog = (route: Log.Route): Data.Remote<number> =>
  pipe(
    use(route),
    Data.Remote.map((violations) => violations.length)
  );

export namespace Response {
  export const runtime = () =>
    Runtime.intersection([
      Container.Response.runtime(),
      Runtime.strict({
        violated_limit: Time.Duration.Response.runtime(),
        violation_start_date: Time.DateTime.Response.runtime(),
        violation_end_date: Runtime.nullable(Time.DateTime.Response.runtime()),
      }),
    ]);

  export const decode = (
    response: Runtime.ToType<typeof runtime>
  ): Violation => ({
    ...Time.Interval.fromDateTimes(response.violation_start_date)(
      response.violation_end_date
    ),

    limit: response.violated_limit,
    container: Container.Response.decode(response),
  });
}

export namespace Container {
  /**
   * `Violation.Container` represents the surrounding time period in which
   * a `Violation` occurred, i.e. the cycle or shift.
   */
  export type Container = {
    readonly type: Type.Type;
    readonly timing: Timing.Timing;
  };

  export namespace Response {
    export const runtime = () =>
      Runtime.intersection([
        Type.Response.runtime(),
        Timing.Response.runtime(),
      ]);

    export const decode = (
      response: Runtime.ToType<typeof runtime>
    ): Container => ({
      type: Type.Response.decode(response),
      timing: Timing.Response.decode(response),
    });
  }

  export namespace Type {
    export type Type = ReturnType<
      | typeof cycleViolation
      | typeof breakViolation
      | typeof driveViolation
      | typeof shiftViolation
    >;

    export const cycleViolation = () => "Cycle Violation" as const;
    export const breakViolation = () => "Break Violation" as const;
    export const driveViolation = () => "Drive Violation" as const;
    export const shiftViolation = () => "Shift Violation" as const;

    export const Component: React.Fn<{
      readonly container: { readonly type: Type };
    }> = ({ container: { type } }) => <>{type}</>;

    export namespace Response {
      export const runtime = () =>
        Runtime.strict({
          violation_type: Runtime.strict({
            name: Runtime.union([
              Runtime.literal(cycleViolation()),
              Runtime.literal(breakViolation()),
              Runtime.literal(driveViolation()),
              Runtime.literal(shiftViolation()),
            ]),
          }),
        });

      export const decode = (response: Runtime.ToType<typeof runtime>): Type =>
        response.violation_type.name;
    }
  }

  export namespace Timing {
    /**
     * Due to back-end limitations, sometimes we only receive a starting
     * `DateTime` instead of a full `Interval`
     */
    export type Timing = Time.Interval | Time.DateTime;

    export namespace Response {
      export const runtime = () =>
        Runtime.strict({
          ongoing: Runtime.boolean(),
          containing_interval_start_date: Time.DateTime.Response.runtime(),
          containing_interval_end_date: Runtime.nullable(
            Time.DateTime.Response.runtime()
          ),
        });

      export const decode = (
        response: Runtime.ToType<typeof runtime>
      ): Timing =>
        pipe(
          Time.Interval.fromDateTimes(response.containing_interval_start_date)(
            response.containing_interval_end_date
          ),
          ({ interval }) =>
            Time.Interval.isStopped({ interval })
              ? { interval }
              : response.ongoing
              ? { interval }
              : interval.start
        );
    }
  }
}
